import { ChangeDetectionStrategy, Component, ElementRef, HostListener, computed, inject } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { PremiumHintService } from './premium-hint.service';

const WIDTH = 260;
const MARGIN = 8;

/**
 * Small popover explaining the Stammgast crown, with a link to /premium.
 * During a game the link opens a new tab so nobody leaves a running round.
 */
@Component({
  selector: 'app-premium-hint',
  standalone: true,
  imports: [RouterModule],
  templateUrl: './premium-hint.component.html',
  styleUrl: './premium-hint.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PremiumHintComponent {
  private hint = inject(PremiumHintService);
  private router = inject(Router);
  private host = inject(ElementRef<HTMLElement>);

  readonly anchor = this.hint.anchor;

  /** below the crown, or above it when there is no room; clamped to the viewport */
  readonly position = computed(() => {
    const anchor = this.anchor();
    if (!anchor) return null;
    const left = Math.min(Math.max(anchor.left - WIDTH / 2, MARGIN), window.innerWidth - WIDTH - MARGIN);
    const below = anchor.bottom + MARGIN;
    const above = window.innerHeight - anchor.top + MARGIN;
    return below + 150 < window.innerHeight ? { left, top: below, bottom: null } : { left, top: null, bottom: above };
  });

  get inGame(): boolean {
    return /^\/(spielen|spiel)(\/|\?|$)/.test(this.router.url);
  }

  close(): void {
    this.hint.close();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.close();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    // crowns open the hint themselves (and stop the click); everything else closes it
    if (this.anchor() && !this.host.nativeElement.contains(event.target as Node)) this.close();
  }

  @HostListener('window:resize')
  @HostListener('window:scroll')
  onViewportChange(): void {
    if (this.anchor()) this.close();
  }
}
